import { isSpeiStrictService, servicioDeGestor } from './gestor-servicio';
import { BalanceEntity } from './balance-entity.model';
import {
  SpeiAssignedEntries,
  SpeiAssignedCounts,
  speiAssignedCounts,
  speiAssignedEntries,
  speiHasNoEntities,
} from './spei/spei-assigned-entry.model';

/**
 * Productos que puede ofrecer la pantalla posterior al login. Cada uno se
 * muestra SOLO si el usuario lo tiene como gestor: la pantalla no es un menú
 * fijo, es el reflejo de su autoridad.
 */
export type ProductKey = 'spei' | 'cards' | 'tpv' | 'ecommerce';

/** Un proveedor SPEI del usuario, con sus conteos. */
export interface ProductProvider {
  /** Código tal como lo emite el gestor (STP, ARCUS, TRANSFER, ASP). */
  code: string;
  companies: number;
  units: number;
  accounts: number;
}

/**
 * Producto listo para pintar. `providers` solo se llena en SPEI —el único con
 * varios rieles—; el resto va vacío. `companies`/`accounts` son `null` cuando
 * no hay dato disponible sin pegarle a la red (Cards, TPV y E-commerce: sus
 * conteos exigirían una petición por producto justo después del login).
 */
export interface SelectableProduct {
  key: ProductKey;
  /** Dashboard del producto: a dónde se entra al elegirlo. */
  route: string;
  providers: ProductProvider[];
  companies: number | null;
  units: number | null;
  accounts: number | null;
  /**
   * SPEI: raíces asignadas de los rieles SIN cuenta padre (products_spei_sin_cuenta_padre).
   * Ausente en los demás productos y cuando el llamador no pasó `assignedEntities`.
   */
  speiEntries?: SpeiAssignedEntries;
  /** SPEI sin NINGUNA entidad de la que partir (R16): aviso en la tarjeta y sin navegar. */
  speiNoEntities?: boolean;
}

/** Autoridad del usuario + entidades en sesión, sin dependencias de Angular. */
export interface ProductAccess {
  hasSpei: boolean;
  hasCards: boolean;
  hasTpv: boolean;
  hasEcommerce: boolean;
  /** Proveedores SPEI asignados (`UserRoleService.getSpeiProviders()`). */
  speiProviders: string[];
  /** `balance_object` de sesión: cuentas padre + entidades sueltas. */
  entities: BalanceEntity[];
  /**
   * `extra_gestor_entities` de sesión, SOLO (R1). Opcional: sin él SPEI es exactamente
   * el de antes (navbar y los llamadores que no lo pasan no cambian).
   */
  assignedEntities?: BalanceEntity[];
}

const PRODUCT_ROUTE: Record<ProductKey, string> = {
  spei: '/dashboard',
  cards: '/cards/dashboard',
  tpv: '/tpv/dashboard',
  // E-commerce aterriza en su dashboard (Pasarela de Pagos), como los demás productos.
  ecommerce: '/e-commerce/dashboard',
};

/** Orden de la pantalla; fijo, no depende del orden de los gestores. */
const PRODUCT_ORDER: ProductKey[] = ['spei', 'cards', 'tpv', 'ecommerce'];

/** `Unidad  de Negocio` llega del backend con doble espacio en algunos endpoints. */
function tipoNormalizado(entity: BalanceEntity): string {
  return String(entity?.entity_type ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function providerNormalizado(valor: unknown): string {
  return String(valor ?? '').trim().toUpperCase();
}

/**
 * Cuenta empresas y cuentas del árbol de UNA entidad raíz. Se recorre en vez de
 * leer `*_count` del backend porque esos campos no están en el contrato tipado
 * y no vienen en todas las formas de la respuesta. El provider se toma de la
 * raíz: los hijos anidados no lo repiten.
 */
function contarArbol(root: BalanceEntity): { companies: number; units: number; accounts: number } {
  let companies = 0;
  let units = 0;
  let accounts = 0;

  const visitar = (entity: BalanceEntity): void => {
    const tipo = tipoNormalizado(entity);
    if (tipo === 'empresa') companies++;
    else if (tipo === 'unidad de negocio') units++;
    else if (tipo === 'cuenta') accounts++;

    const unidadesPrecalculadas = Number(entity.business_units_count ?? NaN);
    const tieneUnidades = Number.isFinite(unidadesPrecalculadas);
    if (tieneUnidades) units += unidadesPrecalculadas;

    // El backend ya calcula `accounts_count` en la Cta_Padre y el arbol que
    // baja NO trae `accounts[]`: sin esta rama las cuentas salen SIEMPRE en
    // cero. Cuando el precalculado esta, se usa y no se recorren las cuentas
    // de ese nodo, para no contarlas dos veces.
    const precalculado = Number(entity.accounts_count ?? NaN);
    const tienePrecalculado = Number.isFinite(precalculado);
    if (tienePrecalculado) accounts += precalculado;

    for (const hijo of entity.companies ?? []) visitar(hijo);
    if (!tieneUnidades) {
      for (const hijo of entity.business_units ?? []) visitar(hijo);
    }
    if (!tienePrecalculado) {
      for (const hijo of entity.accounts ?? []) visitar(hijo);
    }
  };

  visitar(root);
  return { companies, units, accounts };
}

/**
 * Conteos por proveedor SPEI. Dos filtros, y son ACUMULATIVOS:
 *
 * 1. Solo los rieles ASIGNADOS (`speiProviders`): `balance_object` puede traer
 *    padres de otros rieles y ofrecerlos sería prometer acceso que el backend
 *    niega.
 * 2. Solo los rieles que tienen AL MENOS UNA cuenta padre en el inventario. Un
 *    riel puede estar asignado por un gestor de nivel Empresa y no tener
 *    Cta_Padre activa; antes se sembraba su fila igual y salía en ceros. Esa
 *    fila era un destino MUERTO: `selectProvider` no encuentra padre con
 *    `findFatherForProvider` y termina en el dashboard del clic muerto.
 *
 * ⚠️ Esta regla NO es la del conteo de `countFatherAccounts`, y no deben
 * unificarse. El CONTEO decide si la pantalla se muestra y por eso TOLERA la
 * cuenta padre sin `provider` (`refreshBalance` la deja así; ver
 * `retagProviders`). Una FILA, en cambio, es un destino clickable: necesita un
 * `provider` que empate con un riel asignado, o no hay a dónde ir. Si una sesión
 * da conteo 2 con una sola fila, la pantalla se muestra y sigue siendo útil
 * —hay riel que elegir— y ninguna fila queda muerta.
 */
function proveedoresSpei(access: ProductAccess): ProductProvider[] {
  const totales = new Map<string, ProductProvider>();
  for (const code of access.speiProviders.map(providerNormalizado)) {
    if (code && !totales.has(code)) totales.set(code, { code, companies: 0, units: 0, accounts: 0 });
  }

  const conCtaPadre = new Set<string>();
  for (const entity of access.entities ?? []) {
    const code = providerNormalizado(entity?.provider);
    const acumulado = totales.get(code);
    if (!acumulado) continue;
    if (tipoNormalizado(entity) === 'cta_padre') conCtaPadre.add(code);
    const { companies, units, accounts } = contarArbol(entity);
    acumulado.companies += companies;
    acumulado.units += units;
    acumulado.accounts += accounts;
  }

  return [...totales.values()].filter((provider) => conCtaPadre.has(provider.code));
}

/** Productos a mostrar, en orden fijo y solo los que el usuario gestiona. */
export function buildSelectableProducts(access: ProductAccess): SelectableProduct[] {
  const disponible: Record<ProductKey, boolean> = {
    spei: access.hasSpei,
    cards: access.hasCards,
    tpv: access.hasTpv,
    ecommerce: access.hasEcommerce,
  };

  return PRODUCT_ORDER.filter((key) => disponible[key]).map((key) => {
    if (key !== 'spei') {
      return { key, route: PRODUCT_ROUTE[key], providers: [], companies: null, units: null, accounts: null };
    }
    const providers = proveedoresSpei(access);
    const hoy: SpeiAssignedCounts = {
      companies: providers.reduce((total, p) => total + p.companies, 0),
      units: providers.reduce((total, p) => total + p.units, 0),
      accounts: providers.reduce((total, p) => total + p.accounts, 0),
    };
    // Sin `assignedEntities` el producto SPEI es el de siempre, campo a campo.
    if (access.assignedEntities === undefined) {
      return { key, route: PRODUCT_ROUTE.spei, providers, ...hoy };
    }
    const speiEntries = speiAssignedEntries({
      inventory: access.entities ?? [],
      assignedEntities: access.assignedEntities,
      speiProviders: access.speiProviders,
    });
    const speiNoEntities =
      providers.length === 0 &&
      speiEntries.all.length === 0 &&
      speiHasNoEntities({ inventory: access.entities ?? [], assignedEntities: access.assignedEntities });
    return {
      key,
      route: PRODUCT_ROUTE.spei,
      providers,
      ...speiCardCounts(hoy, providers.length, speiEntries, speiNoEntities),
      speiEntries,
      speiNoEntities,
    };
  });
}

/**
 * Conteos de la tarjeta SPEI (R15, R16, R19, R48):
 * - sin cuenta padre: los de lo asignado;
 * - mixta CON la lista de proveedores de hoy (2+ filas de padre): la suma, contador a
 *   contador, `null` si alguno de los sumandos lo es (Q2);
 * - mixta con UNA fila de padre, o con padre: los de hoy, sin tocar;
 * - sin ninguna entidad: los tres "No disponible".
 */
function speiCardCounts(
  hoy: SpeiAssignedCounts,
  filasDePadre: number,
  entries: SpeiAssignedEntries,
  sinEntidades: boolean,
): SpeiAssignedCounts {
  if (sinEntidades) return { companies: null, units: null, accounts: null };
  if (entries.shape === 'without-father') return speiAssignedCounts(entries.all);
  if (entries.shape === 'mixed' && filasDePadre >= 2) {
    const extra = speiAssignedCounts(entries.all);
    const sumar = (a: number | null, b: number | null): number | null => (a === null || b === null ? null : a + b);
    return {
      companies: sumar(hoy.companies, extra.companies),
      units: sumar(hoy.units, extra.units),
      accounts: sumar(hoy.accounts, extra.accounts),
    };
  }
  return hoy;
}

/**
 * La tarjeta SPEI pinta las filas de los rieles sin cuenta padre en la lista de
 * proveedores (R18): SOLO en una sesión mixta que YA tiene la lista de hoy (2+ filas
 * de padre). Con una sola fila de padre no se agrega ningún clic (R48).
 */
export function speiShowsRailsWithoutFather(product: SelectableProduct): boolean {
  return product.speiEntries?.shape === 'mixed' && product.providers.length >= 2;
}

/**
 * Los tres contadores de una tarjeta de producto. `null` significa "sin dato":
 * la pantalla lo pinta como guion largo, nunca como cero.
 */
export interface ProductCounts {
  companies: number | null;
  units: number | null;
  accounts: number | null;
}

/**
 * Sustituye los TRES contadores de un producto y devuelve una lista nueva.
 *
 * Inmutable a propósito: los productos que no se tocan conservan su MISMA
 * referencia, así que quien recomponga la vista desde esta lista no invalida
 * nada más que la tarjeta que cambió. Si `key` no está en la lista se devuelve
 * la misma referencia —un conteo que llega tarde no puede materializar un
 * producto que el usuario no gestiona—.
 *
 * Reemplaza, no fusiona: un `null` vuelve a dejar el contador sin dato, que es
 * como los casos de uso expresan "esto no se puede contar de forma fiable".
 */
export function withProductCounts(
  products: SelectableProduct[],
  key: ProductKey,
  counts: ProductCounts,
): SelectableProduct[] {
  if (!products.some((product) => product.key === key)) return products;
  return products.map((product) =>
    product.key === key
      ? { ...product, companies: counts.companies, units: counts.units, accounts: counts.accounts }
      : product,
  );
}

/**
 * Un contador de una tarjeta: se está pidiendo, se sabe, o no se pudo saber
 * (feature 152). Antes "cargando" y "sin dato" se pintaban con el MISMO guion y
 * no había forma de distinguir una tarjeta en vuelo de una caída. Jamás `0` por
 * ausencia: un `0` solo existe como `value`.
 */
export type ProductCountState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'value'; readonly value: number }
  | { readonly kind: 'absent' };

/** Los tres contadores de una tarjeta, cada uno con su propio estado. */
export interface ProductCountStates {
  companies: ProductCountState;
  units: ProductCountState;
  accounts: ProductCountState;
}

/** Nombre de cada contador de una tarjeta. */
export type ProductCounterKey = keyof ProductCounts;

/** Orden fijo de los contadores en la tarjeta. */
export const PRODUCT_COUNTER_KEYS: readonly ProductCounterKey[] = Object.freeze([
  'companies',
  'units',
  'accounts',
]);

const CARGANDO: ProductCountState = Object.freeze({ kind: 'loading' as const });
const AUSENTE: ProductCountState = Object.freeze({ kind: 'absent' as const });

/**
 * Un valor de contador → su estado. Solo un entero finito ≥ 0 es dato; `null`,
 * `undefined`, `NaN`, negativos, fracciones o cualquier otra cosa son "no
 * disponible". Nada de `Number(x) || 0`.
 */
export function countStateOf(valor: unknown): ProductCountState {
  if (typeof valor === 'number' && Number.isInteger(valor) && valor >= 0) {
    return { kind: 'value', value: valor };
  }
  return AUSENTE;
}

/**
 * Conteos PARCIALES → estados. Una llave PRESENTE ya se resolvió (número o
 * `null` = no disponible); una llave AUSENTE sigue en vuelo mientras `pending`,
 * y al terminar la lectura (`pending = false`) lo que nunca llegó es "no
 * disponible", no un cero ni un guion de carga eterno.
 */
export function countStatesFrom(
  counts: Partial<ProductCounts>,
  pending: boolean,
): ProductCountStates {
  const estado = (key: ProductCounterKey): ProductCountState =>
    Object.prototype.hasOwnProperty.call(counts, key)
      ? countStateOf(counts[key])
      : pending
        ? CARGANDO
        : AUSENTE;
  return { companies: estado('companies'), units: estado('units'), accounts: estado('accounts') };
}

/**
 * ¿La tarjeta pinta el bloque de contadores? Todas salvo E-commerce (Q3 del
 * humano, R45): sus cifras no tienen fuente y "— — —" permanente se leía como
 * una carga que nunca terminaba.
 */
export function productShowsCounts(key: ProductKey): boolean {
  return key !== 'ecommerce';
}

/**
 * Siembra de estados al pintar la pantalla (R13, R14, R44, R45):
 * - SPEI: sus cifras ya están en sesión → `value` desde el primer frame;
 * - Cards y TPV: los TRES contadores en carga hasta que llegue su lectura;
 * - E-commerce: `null`, la tarjeta no lleva contadores.
 */
export function initialCountStates(product: SelectableProduct): ProductCountStates | null {
  if (!productShowsCounts(product.key)) return null;
  if (product.key === 'spei') {
    return countStatesFrom(
      { companies: product.companies, units: product.units, accounts: product.accounts },
      false,
    );
  }
  return countStatesFrom({}, true);
}

/**
 * Todo lo que la decisión de aterrizaje necesita. Se pasa como objeto —y no como
 * dos parámetros sueltos— para que agregar un criterio mañana no vuelva a romper
 * la firma en los dos llamadores.
 */
export interface ProductLandingInput {
  /** Productos que gestiona el usuario (orden fijo de `PRODUCT_ORDER`). */
  products: ProductKey[];
  /**
   * Entidades que el llamador YA tiene resueltas. Solo se cuentan las
   * `Cta_Padre`; el resto (empresa, unidad, cuenta) se ignora, así que se puede
   * pasar el inventario completo sin filtrarlo antes.
   */
  entities: BalanceEntity[];
  /**
   * Rieles SPEI ASIGNADOS al usuario. Sin ellos la tarjeta SPEI no tiene fila
   * alguna que ofrecer, así que el conteo debe medirse contra esta lista.
   */
  speiProviders: string[];
  /**
   * `extra_gestor_entities` (R36). Solo pesa en una sesión solo-SPEI SIN cuenta padre;
   * en cualquier otra forma el resultado es el de antes (R49).
   */
  assignedEntities?: BalanceEntity[];
}

/**
 * Identidad de una cuenta padre para deduplicar. `polipay_account` es el
 * identificador que usa todo el flujo (`cacheFatherAccount`,
 * `findFatherForProvider`); `clabe` es el sustituto habitual cuando el backend no
 * lo manda, y la pareja provider+nombre el último recurso.
 */
function identidadCtaPadre(entity: BalanceEntity): string {
  const polipay = String(entity?.polipay_account ?? '').trim();
  if (polipay) return `polipay:${polipay}`;
  const clabe = String(entity?.clabe ?? '').trim();
  if (clabe) return `clabe:${clabe}`;
  const nombre = String(entity?.id_entity_name ?? '').trim().toLowerCase();
  return `par:${providerNormalizado(entity?.provider)}:${nombre}`;
}

/**
 * Cuántas cuentas padre ÚNICAS y ALCANZABLES hay en una lista de entidades.
 * Puro y O(n).
 *
 * El tipo se reconoce con el criterio que ya usa el resto del flujo
 * (`AvailableAmountService.getFatherAccountsList()` y
 * `VerifyCodeComponent.extractFatherAccountsFromResponse()`): el `entity_type`
 * normalizado. Encima de eso, dos filtros que igualan el conteo con lo que la
 * pantalla puede REALMENTE ofrecer:
 *
 * 1. Se EXCLUYE la cuenta padre cuyo `provider` viene PRESENTE y no está entre
 *    los rieles asignados. Es la fuga cross-provider de
 *    `/api/father_account/balances/`, que devuelve padres de rieles que el
 *    usuario no gestiona: ofrecerlos "sería prometer acceso que el backend
 *    niega" (mismo criterio que `proveedoresSpei()`).
 *
 *    Pero un `provider` VACÍO, nulo o ausente **sí cuenta**. Un dato faltante no
 *    es prueba de que el riel sea ajeno, y aquí el dato falta de forma NORMAL:
 *    `refreshBalance` puede reescribir `father_accounts_list` sin providers —por
 *    eso existe `retagProviders()` en `account-selection.component.ts`, que los
 *    rellena desde los gestores cuando "viene vacío"—. Descartarlos hacía que un
 *    usuario con tres cuentas padre contara 0 y la pantalla se auto-expulsara al
 *    dashboard. El filtro puede EXCLUIR, nunca ZANJAR: no endurecer esto.
 * 2. Sin repetidos. Los llamadores no cuentan sobre el mismo conjunto: el
 *    selector concatena `father_accounts_list` con `extra_gestor_entities`, que
 *    llegan crudos y pueden repetir un padre.
 *
 * Sin rieles asignados (gestor legacy sin `provider`) NO se anula el conteo: se
 * cuentan las cuentas padre que haya.
 */
export function countFatherAccounts(
  entities: BalanceEntity[],
  speiProviders: string[],
): number {
  const asignados = rielesAsignados(speiProviders);

  const unicos = new Set<string>();
  for (const entity of entities ?? []) {
    if (tipoNormalizado(entity) !== 'cta_padre') continue;
    if (esCtaPadreDeRielAjeno(entity, asignados)) continue;
    unicos.add(identidadCtaPadre(entity));
  }
  return unicos.size;
}

/**
 * La misma lista sin las cuentas padre de un riel AJENO (punto 1 de
 * `countFatherAccounts`): lo que el selector de cuenta padre (`/account-selection`)
 * puede ofrecer de verdad. Pintar una cuenta padre de un proveedor que el usuario
 * no tiene asignado "sería prometer acceso que el backend niega".
 *
 * Es la regla de `countFatherAccounts`, no una copia: las dos consultan
 * `esCtaPadreDeRielAjeno`. Un `provider` vacío o ausente SE QUEDA (un dato
 * faltante no prueba que el riel sea ajeno), las entidades que no son `Cta_Padre`
 * no se tocan y sin rieles asignados no se quita nada. No deduplica: el selector
 * pinta lo que trae la sesión. Puro y O(n); conserva el orden y los objetos.
 */
export function withoutForeignFatherAccounts<T extends BalanceEntity>(
  entities: T[],
  speiProviders: string[],
): T[] {
  const asignados = rielesAsignados(speiProviders);
  return (entities ?? []).filter(
    (entity) => tipoNormalizado(entity) !== 'cta_padre' || !esCtaPadreDeRielAjeno(entity, asignados),
  );
}

function rielesAsignados(speiProviders: string[]): Set<string> {
  const asignados = new Set<string>();
  for (const code of speiProviders ?? []) {
    const normalizado = providerNormalizado(code);
    if (normalizado) asignados.add(normalizado);
  }
  return asignados;
}

/** Solo se descarta con evidencia: riel presente Y ajeno a los asignados. */
function esCtaPadreDeRielAjeno(entity: BalanceEntity, asignados: Set<string>): boolean {
  const riel = providerNormalizado(entity?.provider);
  return !!riel && asignados.size > 0 && !asignados.has(riel);
}

/**
 * Cuántos rieles SPEI hay REALMENTE entre los que elegir, según los gestores.
 *
 * Es la segunda señal de la decisión de aterrizaje, y existe porque la lista de
 * cuentas padre NO es fiable en el login: `resolveFatherAccounts()` devuelve
 * `[]` sin error cuando el body de verify no trae `Cta_Padre` y
 * `getUserPolipayFromVerify()` no da con qué pedir el listado, o cuando ningún
 * gestor es de nivel `Cta_Padre`. Con la lista vacía el conteo daba 0 y un
 * usuario con tres rieles aterrizaba en el dashboard sin poder elegir. Los
 * rieles, en cambio, salen de los gestores del propio body: no dependen de
 * ninguna petición.
 *
 * En este producto la Cta_Padre ES el proveedor (ver `findFatherForProvider`),
 * así que más de un riel significa que hay algo que elegir.
 *
 * WHERE el inventario SÍ trae cuentas padre, solo cuentan los rieles que tienen
 * una: es el mismo criterio con el que `proveedoresSpei()` siembra las FILAS
 * (R20), así que el número coincide con lo que la pantalla puede ofrecer y no
 * se manda a nadie a un selector de una sola fila. Cuando no hay ninguna
 * `Cta_Padre` que cruzar, se cae a los rieles asignados: es la mejor señal
 * disponible y es exactamente el caso del defecto.
 */
function rielesConDondeElegir(entities: BalanceEntity[], speiProviders: string[]): number {
  const asignados = rielesAsignados(speiProviders);
  if (asignados.size === 0) return 0;

  const conCtaPadre = new Set<string>();
  let hayCtaPadre = false;
  for (const entity of entities ?? []) {
    if (tipoNormalizado(entity) !== 'cta_padre') continue;
    hayCtaPadre = true;
    const riel = providerNormalizado(entity?.provider);
    if (riel && asignados.has(riel)) conCtaPadre.add(riel);
  }

  return hayCtaPadre ? conCtaPadre.size : asignados.size;
}

/**
 * Landing posterior al login.
 *
 * Con UN SOLO producto no tiene sentido pedir que lo elija... salvo que ese
 * producto sea SPEI y haya MÁS DE UNA cuenta padre —o más de un riel entre los
 * que elegir—: entonces el selector deja de ser una pantalla vacía y pasa a ser
 * el único sitio donde el usuario elige con qué riel entra (`selectProvider`
 * fija `balance_object`, `selectedFatherAccount` y la vista de Envíos). Con
 * varios productos, o con ninguno —para que la pantalla explique el vacío—,
 * siempre manda el selector.
 *
 * Las DOS señales se combinan con un O, y ninguna sobra: el conteo de cuentas
 * padre cubre al usuario cuyos padres llegan SIN `provider` (`refreshBalance`
 * los deja así), y los rieles cubren al usuario cuya lista de cuentas padre
 * llegó vacía. Cualquiera de las dos por sí sola deja fuera un caso real.
 *
 * La cuenta padre y el riel solo pesan en SPEI: la Cta_Padre es la raíz de ese
 * riel. Cards y TPV tienen su propio selector de contexto y su aterrizaje no
 * depende de ella.
 */
export function resolveProductLanding(
  input: ProductLandingInput,
  selectorRoute: string,
): string {
  const [unico, ...resto] = input.products;
  if (!unico || resto.length > 0) return selectorRoute;
  if (unico !== 'spei') return productRoute(unico);

  const hayQueElegir =
    countFatherAccounts(input.entities, input.speiProviders) > 1 ||
    rielesConDondeElegir(input.entities, input.speiProviders) > 1;
  if (hayQueElegir) return selectorRoute;

  // R36 (Q6, la ÚNICA excepción deliberada a R49): solo-SPEI SIN cuenta padre con
  // dos o más raíces asignadas ⇒ se pregunta. Mixta, con padre o sin raíces: lo de hoy.
  if (input.assignedEntities && input.assignedEntities.length > 0) {
    const entradas = speiAssignedEntries({
      inventory: input.entities ?? [],
      assignedEntities: input.assignedEntities,
      speiProviders: input.speiProviders,
    });
    if (entradas.shape === 'without-father' && entradas.all.length > 1) return selectorRoute;
  }
  return productRoute(unico);
}

/**
 * ¿Hay que preguntar con qué cuenta padre SPEI se entra a una pantalla de SPEI
 * cuando la sesión todavía no eligió ninguna? (p. ej. el usuario entró por Cards
 * desde `/products` y abre "Unidades y cuentas" o "Transacciones" del menú Suite).
 *
 * Es la MISMA pregunta que hace `resolveProductLanding` para SPEI —más de una
 * cuenta padre alcanzable, o más de un riel asignado con cuenta padre—, con UNA
 * diferencia deliberada: aquí se exige que el inventario de sesión traiga al
 * menos una `Cta_Padre`. El login cae a los rieles asignados cuando la lista llega
 * vacía porque `/products` puede explicar ese vacío; el selector de cuentas padre
 * (`/account-selection`) no tiene NADA que ofrecer sin cuentas padre en sesión
 * (su tarjeta de respaldo no navega), así que mandar ahí sería un callejón.
 */
export function speiProviderChoiceNeeded(
  entities: BalanceEntity[],
  speiProviders: string[],
): boolean {
  const hayCtaPadre = (entities ?? []).some((entity) => tipoNormalizado(entity) === 'cta_padre');
  if (!hayCtaPadre) return false;
  return (
    countFatherAccounts(entities, speiProviders) > 1 ||
    rielesConDondeElegir(entities, speiProviders) > 1
  );
}

/** Selector de cuenta padre / proveedor SPEI (acepta `?returnUrl=` interno). */
export const SPEI_PROVIDER_SELECTION_ROUTE = '/account-selection';

/** El acordeón de proveedores solo aparece si hay más de uno que elegir. */
export function tieneAcordeon(product: SelectableProduct): boolean {
  return product.providers.length > 1;
}

/** Gestor tal como llega en el objeto user; solo interesan `service`, `provider` y su nivel. */
export interface GestorLike {
  service?: string | null;
  provider?: string | null;
  entity_type?: string | null;
}

function esGestorDeCtaPadre(gestor: GestorLike | null | undefined): boolean {
  return String(gestor?.entity_type ?? '').replace(/\s+/g, ' ').trim().toLowerCase() === 'cta_padre';
}

/**
 * ¿Algún gestor es de nivel `Cta_Padre` con servicio SPEI ESTRICTO? (R27, R28, Q9). Sin
 * él, banca no devuelve ninguna cuenta padre SPEI: pedir `/api/father_account/` sería
 * una consulta de nivel superior que el usuario no tiene.
 */
export function hasSpeiFatherGestor(gestores: GestorLike[] | null | undefined): boolean {
  return (gestores ?? []).some((gestor) => esGestorDeCtaPadre(gestor) && isSpeiStrictService(gestor?.service));
}

/**
 * ¿Hay un riel SPEI sin cuenta padre según los gestores? (R39): un gestor SPEI estricto
 * que NO es de `Cta_Padre` cuyo `provider` no tiene gestor SPEI estricto de `Cta_Padre`.
 */
export function hasSpeiRailWithoutFatherGestor(gestores: GestorLike[] | null | undefined): boolean {
  const conPadre = new Set<string>();
  const sinPadre = new Set<string>();
  for (const gestor of gestores ?? []) {
    if (!isSpeiStrictService(gestor?.service)) continue;
    const riel = providerNormalizado(gestor?.provider);
    if (!riel) continue;
    (esGestorDeCtaPadre(gestor) ? conPadre : sinPadre).add(riel);
  }
  for (const riel of sinPadre) if (!conPadre.has(riel)) return true;
  return false;
}

/**
 * Productos que dan los gestores, sin tocar sesion ni red. Lo usa el login para
 * decidir el aterrizaje con los gestores del body de verify, antes de que haya
 * balances resueltos.
 *
 * SPEI en sentido ESTRICTO: `service === 'SPEI'` o gestor legacy sin `service`. Un
 * servicio que el front no reconoce NO da producto: si cayera en SPEI, el login
 * aterrizaría a un usuario en una ruta que las guardas le rechazan.
 * Ojo, difiere de `UserRoleService.hasSpeiGestor()`, que agrupa CARDS y
 * PAYMENTS a proposito (semantica de "tiene saldo"): con ESA regla un usuario
 * solo-Cards veria tambien el producto SPEI, que no gestiona.
 */
export function productKeysFromGestores(gestores: GestorLike[]): ProductKey[] {
  const keys = new Set<ProductKey>();
  for (const gestor of gestores ?? []) {
    const service = servicioDeGestor(gestor?.service);
    if (service === null) continue;
    if (service === 'TPV') keys.add('tpv');
    else if (service === 'CARDS') keys.add('cards');
    else if (service === 'PAYMENTS') keys.add('ecommerce');
    else keys.add('spei');
  }
  return PRODUCT_ORDER.filter((key) => keys.has(key));
}

/**
 * Proveedores SPEI ASIGNADOS segun los gestores, sin tocar sesion ni red.
 *
 * Espejo PURO de `UserRoleService.getSpeiProviders()`: gestor con `service`
 * vacio (legacy) o `SPEI`, `provider` no vacio, sin duplicados y en orden de
 * aparicion. Existe porque el login NO puede usar el servicio: `getSpeiProviders`
 * lee la llave `user` de sesion, y en el flujo `_identify` verify-code escribe
 * `user_identify`, asi que ahi devolveria vacio y el aterrizaje se degradaria en
 * silencio. Los gestores del body son la fuente de verdad en ese momento.
 */
export function speiProvidersFromGestores(gestores: GestorLike[]): string[] {
  const vistos = new Set<string>();
  const asignados: string[] = [];
  for (const gestor of gestores ?? []) {
    // La regla ÚNICA de "gestor SPEI" (R42): misma semántica de antes.
    if (!isSpeiStrictService(gestor?.service)) continue;
    const provider = String(gestor?.provider ?? '').trim();
    if (!provider || vistos.has(provider)) continue;
    vistos.add(provider);
    asignados.push(provider);
  }
  return asignados;
}

/** Ruta del selector de producto. Aqui para que la use quien navegue hacia el. */
export const PRODUCT_SELECTION_ROUTE = '/products';

/** Dashboard de un producto. Unica fuente de las rutas por producto. */
export function productRoute(key: ProductKey): string {
  return PRODUCT_ROUTE[key];
}
